import React, { Component, useState, useRef, useEffect, } from 'react';
import { Button } from 'reactstrap';
import { isEmpty, get, pick, omit, last, groupBy, sortBy, keyBy } from 'lodash';
import qs from 'qs';
import classnames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { useNetworkState, useToggle, useAsync, usePrevious, } from 'react-use';
import { toast, } from 'react-toastify';
import ipRangeCheck from 'ip-range-check';

import { fetchIp, } from '../../utils';
import { leftTrialDays as _leftTrialDays, periodOfFiscalYear } from '../../shared/util';
import { isTrialExpired } from '../../shared/models/company';
import firebase, { functions } from '../../firebase';
import { getRole } from '../../shared/models/user';
import { accountCategories } from '../../shared/config';
import { canReadAccountItemAuditorSetting, canWriteCustomSection, } from '../../abilities';
import { defaultTargetMonthOfPeriod, fullPathWithParams } from '../../utils';
import HeaderNav from '../HeaderNav';
import HelpLink from '../HelpLink';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useQueryParams from '../hooks/useQueryParams';
import usePublicPageAccessToken from '../hooks/usePublicPageAccessToken';
import PublicPagePasswordForm from '../PublicPagePasswordForm';

const { keys } = Object;
const db = firebase.firestore();
const usersRef = db.collection('users');
const companiesRef = db.collection('companies');
const getCompany = functions.httpsCallable('getCompany');
const periodFromSearch = (search) => {
  const { period } = qs.parse(search.slice(1), { arrayLimit: Infinity });
  return parseInt(period, 10);
};
const targetMonthFromSearch = (search) => {
  const { targetMonth } = qs.parse(search.slice(1), { arrayLimit: Infinity });
  return parseInt(targetMonth, 10);
};

export default function CompanyPublicPageHOC(WrappedComponent) {
  return function CompanyPublicPage (props) {
    const { history, location, user, } = props;
    const queryParams = useQueryParams();
    const { 
      subjectType,
      publicKey,
      period: periodString,
      targetMonth: targetMonthString,
    } = queryParams;
    const period = periodString && parseInt(periodString, 10);
    const prevPeriod = usePrevious(period);
    const targetMonth = targetMonthString && parseInt(targetMonthString, 10);
    const headerNavRef = useRef(null);
    const { accessToken, saveAccessToken, } = usePublicPageAccessToken(publicKey);
    const { value: company } = useAsync(async () => {
      const { data: company } = await getCompany({ subjectType, publicKey, accessToken, });
      if(company == null || company.requiresPassword) return company;

      return { ...company, createdAt: { ...company.createdAt, toDate: _ => new Date(company.createdAt._seconds * 1000), } };
    }, [subjectType, publicKey, accessToken]);
    useEffect(() => {
      if(period) return;

      const newPeriod = period || periodOfFiscalYear(last(company?.fiscalYears));
      if(!newPeriod) return;

      const path = fullPathWithParams({ period: newPeriod, }, location);
      history.replace(encodeURI(path));
    }, [company]);
    useEffect(() => {
      if(prevPeriod !== period || !targetMonth) {
        const newTargetMonth = defaultTargetMonthOfPeriod(period, company?.fiscalYears);
        if(newTargetMonth) {
          const path = fullPathWithParams({ targetMonth: newTargetMonth, }, location);
          history.replace(encodeURI(path));
        }
      }
    }, [company, period]);

    if(company?.requiresPassword) {
      return <PublicPagePasswordForm {...{ publicKey, onVerified: saveAccessToken, }} />;
    }

    return company != null && !company.disablesBudgetsShare && period != null && (
      <div>
        <HeaderNav ref={headerNavRef} isPublic {...{ ...props, user, company, period, targetMonth, }} />
        {
          !isTrialExpired(company) && (
            <WrappedComponent {...{
              ...props,
              user,
              company,
              period,
              targetMonth,
              accessToken,
            }}
            />
          )
        }
      </div>
    );
  };
}
